/**
 * In-memory MFN evaluation context. Powers the what-if simulator and conflict
 * radar — both run the deterministic `@argus/engine` comparator client-side
 * (no AI key required) over the fund's current clauses / provisions /
 * commitments, and enrich the raw trigger results with LP names, commitments,
 * and a compliance severity so the UI can reason about them.
 */
import {
  evaluateMfn,
  ECONOMIC_CATEGORIES,
  type CandidateProvision,
  type MfnClauseInput,
  type MfnTriggerResult,
  type ProvisionCategory,
  type StructuredValue,
} from "@argus/engine";
import { listMfnClausesByFund } from "@/lib/api/mfn";
import { listProvisionsByFund } from "@/lib/api/provisions";
import { commitmentMap, listCommitmentsByFund } from "@/lib/api/commitments";
import { listLps, type Lp } from "@/lib/api/lps";

export type Severity = "breach" | "elevated" | "review";

export interface FundMfnContext {
  fundId: string;
  clauses: MfnClauseInput[];
  candidates: CandidateProvision[];
  commitments: Record<string, number>;
  lpsById: Map<string, Lp>;
  /** holder LP ids that hold at least one MFN clause in this fund */
  mfnHolderLpIds: Set<string>;
}

export interface EnrichedTrigger extends MfnTriggerResult {
  holderName: string;
  beneficiaryName: string;
  holderCommitment: number | null;
  beneficiaryCommitment: number | null;
  severity: Severity;
  isEconomic: boolean;
  /** present only for hypothetical (simulated) triggers */
  hypothetical?: boolean;
}

export async function gatherFundMfnContext(
  tenantId: string,
  fundId: string,
): Promise<FundMfnContext> {
  const [clauseRows, provisionRows, commitmentRows, lps] = await Promise.all([
    listMfnClausesByFund(tenantId, fundId),
    listProvisionsByFund(tenantId, fundId, { activeOnly: true }),
    listCommitmentsByFund(tenantId, fundId),
    listLps(tenantId),
  ]);

  const clauses: MfnClauseInput[] = clauseRows.map((c) => ({
    id: c.id,
    holderLpId: c.lp_id,
    scope: c.scope,
    thresholdCommitmentUsd:
      c.threshold_commitment_usd !== null ? Number(c.threshold_commitment_usd) : null,
    excludedCategories: c.excluded_categories ?? [],
    fundScope: c.fund_scope,
    sizeBased: Boolean((c.scope_details as { size_based?: boolean } | null)?.size_based),
  }));

  const candidates: CandidateProvision[] = provisionRows.flatMap((p) =>
    p.lp_id === null
      ? []
      : [
          {
            id: p.id,
            lpId: p.lp_id,
            category: p.category,
            structuredValue: p.structured_value,
            summary: p.summary,
          },
        ],
  );

  const lpsById = new Map(lps.map((l) => [l.id, l]));

  return {
    fundId,
    clauses,
    candidates,
    commitments: commitmentMap(commitmentRows),
    lpsById,
    mfnHolderLpIds: new Set(clauses.map((c) => c.holderLpId)),
  };
}

function severityOf(t: MfnTriggerResult): Severity {
  const economic = ECONOMIC_CATEGORIES.includes(t.category);
  if (economic && t.confidence >= 0.85) return "breach";
  if (economic || t.confidence >= 0.85) return "elevated";
  return "review";
}

function lpName(ctx: FundMfnContext, id: string): string {
  const lp = ctx.lpsById.get(id);
  return lp?.short_name ?? lp?.legal_name ?? "Unknown LP";
}

export function enrich(ctx: FundMfnContext, triggers: MfnTriggerResult[]): EnrichedTrigger[] {
  return triggers
    .map((t) => ({
      ...t,
      holderName: lpName(ctx, t.holderLpId),
      beneficiaryName: lpName(ctx, t.beneficiaryLpId),
      holderCommitment: ctx.commitments[t.holderLpId] ?? null,
      beneficiaryCommitment: ctx.commitments[t.beneficiaryLpId] ?? null,
      severity: severityOf(t),
      isEconomic: ECONOMIC_CATEGORIES.includes(t.category),
    }))
    .sort((a, b) => {
      const rank = { breach: 0, elevated: 1, review: 2 } as const;
      if (rank[a.severity] !== rank[b.severity]) return rank[a.severity] - rank[b.severity];
      return b.confidence - a.confidence;
    });
}

/** Live evaluation against the fund's current state (no persistence). */
export function evaluateLive(ctx: FundMfnContext): EnrichedTrigger[] {
  const triggers = evaluateMfn({
    clauses: ctx.clauses,
    provisions: ctx.candidates,
    commitments: ctx.commitments,
  });
  return enrich(ctx, triggers);
}

export interface HypotheticalGrant {
  lpId: string;
  category: ProvisionCategory;
  structuredValue: StructuredValue;
}

export interface SimulationResult {
  /** triggers caused specifically by the hypothetical grant (who can now elect it) */
  newlyEntitled: EnrichedTrigger[];
  /** total triggers in the hypothetical world */
  allTriggers: EnrichedTrigger[];
  /** baseline triggers without the grant */
  baseline: EnrichedTrigger[];
  /** sum of commitments of the LPs newly entitled to elect */
  exposedCommitment: number;
}

const HYPO_ID = "__hypothetical__";

/**
 * "If we grant LP X this term, who else can elect it under their MFN?"
 * Injects a synthetic provision for the grantee and diffs against baseline.
 */
export function simulateGrant(ctx: FundMfnContext, grant: HypotheticalGrant): SimulationResult {
  const baseline = evaluateLive(ctx);

  const hypoCandidate: CandidateProvision = {
    id: HYPO_ID,
    lpId: grant.lpId,
    category: grant.category,
    structuredValue: grant.structuredValue,
    summary: "Hypothetical grant under evaluation",
  };

  const withGrant = evaluateMfn({
    clauses: ctx.clauses,
    provisions: [
      ...ctx.candidates.filter((c) => c.lpId !== grant.lpId || c.category !== grant.category),
      hypoCandidate,
    ],
    commitments: ctx.commitments,
  });

  const allTriggers = enrich(ctx, withGrant);
  // Holders newly entitled = triggers whose triggering provision is the hypo grant.
  const newlyEntitled = allTriggers
    .filter((t) => t.triggeringProvisionId === HYPO_ID)
    .map((t) => ({ ...t, hypothetical: true }));

  const exposedCommitment = newlyEntitled.reduce((sum, t) => sum + (t.holderCommitment ?? 0), 0);

  return {
    newlyEntitled,
    allTriggers,
    baseline,
    exposedCommitment,
  };
}
